import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { RootState, useAppSelector } from 'src/store';
import { useAutomationsApi } from '../api';
import { resetError, resetList, setError, setFilter, setList, setLoading } from '../slices';

const getServerSortField = (sort?: string) => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    dateCreated: 'date_created',
    dateUpdated: 'date_updated',
    appliedCount: 'applied_count',
    dateLastApplied: 'last_applied',
  };

  const [field, order] = sort.split('_');
  const serverField = sortMap[field] || field;
  return `${serverField}_${order}`;
};

export const useAutomationsService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const { loading, filter, list, error } = useAppSelector(state => state.automations);

  const api = useAutomationsApi();

  const getAutomations = useCallback(async (companyId: string) => {
    const filter = getState().automations.filter;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getList: true }));

      const response = await api.getAutomations({
        companyId,
        status: filter.status,
        sort: getServerSortField(filter.sort) || 'date_created_desc',
        limit: filter.limit,
        offset: filter.offset,
        query: filter.query || undefined,
      });
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getList: false }));
    }
  }, []);

  const changeAutomationsFilter = useCallback(
    (filter: Partial<RootState['automations']['filter']>) => {
      dispatch(setFilter(filter));
    },
    [],
  );

  const resetAutomations = useCallback(() => {
    dispatch(resetList());
  }, []);

  return {
    loading,
    filter,
    list,
    error,
    getAutomations,
    changeAutomationsFilter,
    resetAutomations,
  };
};
